/**
 * Room Merge Tool — "merge rooms"
 *
 * The user clicks rooms to add/remove them from a working set (same
 * Redux-driven selection flow as OutlineTool); a live preview shows the
 * merged boundary of every connected group in the selection. Pressing Enter
 * commits: per group, the largest source room keeps its identity (name,
 * color, tag folder, structure links) and receives the merged polygon, the
 * other source rooms are deleted. Rooms whose walls are farther apart than
 * the configured wall thickness stay separate.
 */

import { OutlineTool } from './OutlineTool';
import { ToolSettingsSchema } from '../../ToolSettings';
import { mergeRoomPolygons, RoomMergeGroup } from '../../../utils/WallRegionBuilder';
import { mergeRoomsInScene } from '../../../utils/mergeRooms';
import { t } from '../../../translation/markers';

export class RoomMergeTool extends OutlineTool {
  override readonly name: string = 'room_merge';

  /** Merge groups computed by the last `refreshPreview` run (all groups,
   *  singletons included) — read by `dispatchPhase` to distinguish an
   *  adjacent selection from a disjoint one. */
  private lastMergeGroups: RoomMergeGroup[] = [];

  override getSettingsSchema(): ToolSettingsSchema {
    // Reuse the inherited thickness slider (same bounds + unit handling); it
    // means "thickest wall that still merges" here rather than an inflation.
    const schema = super.getSettingsSchema();
    const thickness = schema.wallThickness;
    if (thickness.type === 'slider') {
      schema.wallThickness = {
        ...thickness,
        label: t('Wall thickness'),
        description: 'Maximum thickness of the walls absorbed between merged rooms',
      };
    }
    return schema;
  }

  protected override dispatchPhase(): void {
    const selectedCount = this.getSelectedRooms().length;
    let phase = 'idle';
    if (selectedCount === 1) {
      phase = 'needs_more';
    } else if (selectedCount >= 2) {
      phase = this.lastMergeGroups.some((g) => g.roomIdentifiers.length >= 2) ? 'ready' : 'not_adjacent';
    }
    this.dispatchStateChanged({ phase });
  }

  protected override refreshPreview(): void {
    this.clearPreview();
    this.lastComponents = null;
    this.lastMergeGroups = [];

    const floorPlane = this.currentFloorFloorPlane;
    if (!floorPlane) return;

    const rooms = this.getSelectedRooms();
    if (rooms.length < 2) return;

    this.lastMergeGroups = mergeRoomPolygons(
      rooms.map((r) => ({ identifier: r.identifier, polygon: r.getPolygon() })),
      this.wallThickness,
    );
    const merged = this.lastMergeGroups.filter((g) => g.roomIdentifiers.length >= 2);
    if (merged.length === 0) return;

    this.renderPreview(
      merged.map((g) => ({ outerRing: g.outerRing, holes: [] })),
      floorPlane.height,
    );
  }

  protected override commit(): void {
    // The heavy lifting (grouping, largest-room-keeps-identity, deletes,
    // selection handover) is shared with the context-menu merge path.
    mergeRoomsInScene(this.viewer, this.getSelectedRooms(), this.wallThickness);
  }
}
